import { useEffect, useState } from 'react';
import axios from 'axios';
import vopayApi from '@/services/api.config';
import {
  VenmoWithdrawTransactionResponse,
  VenmoWithdrawRequest,
  VenmoWithdrawResponse,
  VenmoRecipientsRequest,
  VenmoRecipientsResponse,
  VenmoDefaultRecipientRequest,
  VenmoDefaultRecipientResponse,
} from 'vopay';

const { api } = vopayApi;

// Venmo Hooks
export function useVenmo({ bearer }: { bearer?: string } = {}) {
  const source = axios.CancelToken.source();

  // Transaction
  const [transaction, setTransaction] = useState<VenmoWithdrawTransactionResponse>();
  const [transactionLoading, setTransactionLoading] = useState(false);

  // Recipients / Withdraw
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipients, setRecipients] = useState<VenmoRecipientsResponse>();

  // Default Recipient
  const [defaultRecipientLoading, setDefaultRecipientLoading] = useState(false);
  const [defaultRecipient, setDefaultRecipient] = useState<VenmoDefaultRecipientResponse>();

  useEffect(() => {
    return () => {
      source.cancel();
    };
  }, []);

  async function fetchVenmoWithdrawTransaction(transactionId: string) {
    try {
      setTransactionLoading(true);
      const res = await api.fetchVenmoWithdrawTransaction({ TransactionID: transactionId }, bearer, source);
      if (!res.Success) {
        setTransaction(undefined);
        throw new Error(res.ErrorMessage);
      }
      setTransaction(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setTransactionLoading(false);
    }
  }

  async function venmoWithdraw(params: VenmoWithdrawRequest): Promise<VenmoWithdrawResponse> {
    try {
      setWithdrawLoading(true);
      const res = await api.postVenmoWithdraw(params, source);
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setWithdrawLoading(false);
    }
  }

  async function fetchVenmoRecipients(params: VenmoRecipientsRequest = {}) {
    try {
      setRecipientsLoading(true);
      const res = await api.fetchVenmoRecipients(params, source);
      if (!res.Success) {
        setRecipients(undefined);
        throw new Error(res.ErrorMessage);
      }
      setRecipients(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setRecipientsLoading(false);
    }
  }

  async function fetchVenmoDefaultRecipient(
    params: VenmoDefaultRecipientRequest = {},
  ): Promise<VenmoDefaultRecipientResponse> {
    try {
      setDefaultRecipientLoading(true);
      const res = await api.fetchVenmoDefaultRecipient(params, source);
      if (!res.Success) {
        setDefaultRecipient(undefined);
        throw new Error(res.ErrorMessage);
      }
      setDefaultRecipient(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setDefaultRecipientLoading(false);
    }
  }

  return {
    // Transaction
    transaction,
    transactionLoading,
    fetchVenmoWithdrawTransaction,

    // Recipients / Withdraw
    withdrawLoading,
    recipientsLoading,
    recipients,
    venmoWithdraw,
    fetchVenmoRecipients,

    // Default Recipient
    defaultRecipientLoading,
    defaultRecipient,
    fetchVenmoDefaultRecipient,
  };
}

// Default export for backward compatibility
export default useVenmo;
